import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Cookie, Shield, BarChart3, Target } from 'lucide-react';
import { toast } from 'sonner';

export default function CookiePreferences() {
  const [consent, setConsent] = useState(null);
  const [consentDate, setConsentDate] = useState(null);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    personalization: false
  });

  useEffect(() => {
    // Load saved consent
    const savedConsent = localStorage.getItem('cookieConsent');
    const savedDate = localStorage.getItem('cookieConsentDate');
    setConsent(savedConsent);
    setConsentDate(savedDate);

    // Load preferences
    const savedPreferences = localStorage.getItem('cookiePreferences');
    if (savedPreferences) {
      setPreferences(JSON.parse(savedPreferences));
    }
  }, []);

  const handleSavePreferences = () => {
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
    localStorage.setItem('cookieConsent', 'custom');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setConsent('custom');
    setConsentDate(new Date().toISOString());
    toast.success('Cookie preferences saved successfully!');
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      personalization: true
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setConsent('accepted');
    setConsentDate(new Date().toISOString());
    toast.success('All cookies accepted!');
  };

  const handleRejectAll = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
      personalization: false
    };
    setPreferences(essentialOnly);
    localStorage.setItem('cookiePreferences', JSON.stringify(essentialOnly));
    localStorage.setItem('cookieConsent', 'declined');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setConsent('declined');
    setConsentDate(new Date().toISOString());
    toast.success('Non-essential cookies declined!');
  };

  return (
    <div className="min-h-screen bg-stone-50 py-8 sm:py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
              <Cookie className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-playfair text-3xl sm:text-4xl font-bold text-primary">
                Cookie Preferences
              </h1>
              <p className="font-outfit text-gray-600">
                Manage how we use cookies on your device
              </p>
            </div>
          </div>

          {consent && (
            <Card className="p-4 bg-blue-50 border-blue-200">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-outfit text-sm text-blue-900">
                    <strong>Current Status:</strong>{' '}
                    {consent === 'accepted' && 'All cookies accepted'}
                    {consent === 'declined' && 'Only essential cookies'}
                    {consent === 'custom' && 'Custom preferences'}
                  </p>
                  {consentDate && (
                    <p className="font-outfit text-xs text-blue-700 mt-1">
                      Last updated: {new Date(consentDate).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </Card>
          )}
        </div>

        {/* Cookie Categories */}
        <div className="space-y-4">
          {/* Essential Cookies */}
          <Card className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-playfair text-lg font-bold text-gray-900">
                      Essential Cookies
                    </h3>
                    <span className="text-xs font-semibold px-2 py-1 bg-green-100 text-green-700 rounded-full">
                      Always Active
                    </span>
                  </div>
                  <p className="font-outfit text-sm text-gray-600 mb-2">
                    These cookies are necessary for the website to function and cannot be switched off. 
                    They are usually only set in response to actions made by you such as setting your privacy preferences, logging in, or filling in forms.
                  </p>
                  <p className="font-outfit text-xs text-gray-500">
                    Examples: Authentication, security, load balancing
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.essential}
                disabled
                className="flex-shrink-0"
              />
            </div>
          </Card>

          {/* Analytics Cookies */}
          <Card className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-playfair text-lg font-bold text-gray-900 mb-2">
                    Analytics Cookies
                  </h3>
                  <p className="font-outfit text-sm text-gray-600 mb-2">
                    These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. 
                    This helps us improve the website's performance and user experience.
                  </p>
                  <p className="font-outfit text-xs text-gray-500">
                    Examples: Google Analytics, page views, session duration
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.analytics}
                onCheckedChange={(checked) => setPreferences({ ...preferences, analytics: checked })}
                className="flex-shrink-0"
              />
            </div>
          </Card>

          {/* Marketing Cookies */}
          <Card className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-playfair text-lg font-bold text-gray-900 mb-2">
                    Marketing Cookies
                  </h3>
                  <p className="font-outfit text-sm text-gray-600 mb-2">
                    These cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user.
                  </p>
                  <p className="font-outfit text-xs text-gray-500">
                    Examples: Facebook Pixel, Google Ads, retargeting
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.marketing}
                onCheckedChange={(checked) => setPreferences({ ...preferences, marketing: checked })}
                className="flex-shrink-0"
              />
            </div>
          </Card>

          {/* Personalization Cookies */}
          <Card className="p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-playfair text-lg font-bold text-gray-900 mb-2">
                    Personalization Cookies
                  </h3>
                  <p className="font-outfit text-sm text-gray-600 mb-2">
                    These cookies enable the website to provide enhanced functionality and personalization. 
                    They may be set by us or by third-party providers whose services we have added to our pages.
                  </p>
                  <p className="font-outfit text-xs text-gray-500">
                    Examples: Language preferences, theme settings, recommendations
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.personalization}
                onCheckedChange={(checked) => setPreferences({ ...preferences, personalization: checked })}
                className="flex-shrink-0"
              />
            </div>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleRejectAll}
            variant="outline"
            className="flex-1 font-outfit"
          >
            Reject Non-Essential
          </Button>
          <Button
            onClick={handleSavePreferences}
            variant="outline"
            className="flex-1 font-outfit border-primary text-primary hover:bg-primary/10"
          >
            Save Preferences
          </Button>
          <Button
            onClick={handleAcceptAll}
            className="flex-1 font-outfit bg-primary hover:bg-primary/90"
          >
            Accept All
          </Button>
        </div>

        {/* Additional Information */}
        <Card className="mt-8 p-6 bg-gray-50">
          <h3 className="font-playfair text-lg font-bold text-gray-900 mb-3">
            More Information
          </h3>
          <div className="font-outfit text-sm text-gray-600 space-y-2">
            <p>
              For more information about how we use cookies and protect your privacy, 
              please read our <a href="/faq" className="text-primary hover:underline">Privacy Policy</a>.
            </p>
            <p>
              You can change your cookie preferences at any time by returning to this page. 
              Note that blocking some types of cookies may impact your experience on our website.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
